/**
 * Language selector for the global header.
 * Persists choice in localStorage (grid_locale) and allows switching between en/es.
 */

import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { SupportedLng } from '../../i18n';

const LOCALES: { value: SupportedLng; labelKey: string }[] = [
  { value: 'en', labelKey: 'language_en' },
  { value: 'es', labelKey: 'language_es' },
];

export default function LanguageSelector() {
  const { t, i18n } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const currentLng = (i18n.language?.split('-')[0] || 'en') as SupportedLng;
  const effectiveLng = currentLng === 'es' ? 'es' : 'en';

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1 px-2 py-1.5 rounded-md text-ink hover:bg-surface-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
        aria-label={t('language')}
        aria-expanded={open}
      >
        <span className="text-base" aria-hidden>🌐</span>
        <span className="font-medium uppercase">{effectiveLng}</span>
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-1 bg-surface border border-line rounded-lg shadow-lg py-1 z-[9999] min-w-[140px]"
          role="listbox"
        >
          {LOCALES.map(({ value, labelKey }) => (
            <button
              key={value}
              type="button"
              role="option"
              aria-selected={effectiveLng === value}
              onClick={() => {
                i18n.changeLanguage(value);
                setOpen(false);
              }}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-surface-2 ${
                effectiveLng === value ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-medium' : 'text-ink'
              }`}
            >
              {t(labelKey)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
